import { Component, NgZone, OnDestroy, OnInit, Renderer2, RendererStyleFlags2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Observable, asyncScheduler, of } from 'rxjs';
import { map, observeOn, shareReplay, tap } from 'rxjs/operators';
import { GlobalSideNavComponent } from 'Src/ng2/shared/layouts/global-side-nav/global-side-nav.component';
import { GlobalTopNavComponent } from 'Src/ng2/shared/layouts/global-top-nav/global-top-nav.component';
import { NvSubNavModule } from 'Src/ng2/shared/components/nv-sub-nav/nv-sub-nav.module';
import { ActionButtonConfig, SegmentedControlConfig, SubNavFilterConfig } from 'Src/ng2/shared/components/nv-sub-nav/nv-sub-nav.component';
import { PageBannerService } from 'Src/ng2/shared/components/page-banner/page-banner.service';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { TValidPartnerTypes } from 'Src/ng2/shared/typings/interfaces/partner.interface';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'op-standard-layout',
  templateUrl: './op-standard-layout.component.html',
  styleUrls: ['./op-standard-layout.component.scss'],
  imports: [RouterModule, GlobalTopNavComponent, GlobalSideNavComponent, NvSubNavModule, AsyncPipe],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
})
export class OpStandardLayoutComponent implements OnInit, OnDestroy {
  // Render the page sub-nav (tabs/filters/etc.) below the top nav. Pages publish their sub-nav via the
  // root PageBannerService (e.g. nv-planning calls setSubNav with its tabs), so the One Portal layout
  // mirrors the legacy school-standard-layout wiring to surface the same config.
  public subNavConfig$: Observable<any> = of({});
  public navigationTabs$: Observable<any[]> = of([]);
  public showSubNav$: Observable<boolean> = of(false);
  // Emits the config only when the sub-nav has content to show (otherwise null),
  // so a single template @if both gates visibility and binds the config.
  public visibleSubNavConfig$: Observable<any> = of(null);
  public origin = 'OpStandardLayout';
  public portal: TValidPartnerTypes;

  private currentSlideToggleConfig: any = null;
  private currentSegmentedControlConfig: SegmentedControlConfig | null = null;
  private currentActionButtonConfig: ActionButtonConfig | null = null;
  private documentPushObserver: MutationObserver | null = null;
  private documentPushUnlistenResize: (() => void) | null = null;
  private documentPushResyncTimeouts: number[] = [];

  constructor (
    private pageBannerService: PageBannerService,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private renderer: Renderer2,
  ) {}

  ngOnInit (): void {
    this.portal = UrlNavigationService.getEntityType(this.activatedRoute.snapshot) as TValidPartnerTypes;

    this.watchDocumentPush();

    this.subNavConfig$ = this.pageBannerService.getSubNavConfig().pipe(
      observeOn(asyncScheduler), // Defer emissions to avoid change detection errors
      tap(config => {
        // Store the configs so the sub-nav callbacks can be invoked on user interaction.
        // (Filter callbacks are invoked by NvSubNavComponent itself, so the layout doesn't store/dispatch them.)
        this.currentSlideToggleConfig = config.slideToggle;
        this.currentSegmentedControlConfig = config.segmentedControl || null;
        this.currentActionButtonConfig = config.actionButton || null;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.navigationTabs$ = this.subNavConfig$.pipe(map(config => config.tabs));
    this.showSubNav$ = this.subNavConfig$.pipe(map(config => this.shouldShowSubNav(config)));
    this.visibleSubNavConfig$ = this.subNavConfig$.pipe(
      map(config => (this.shouldShowSubNav(config) ? config : null)),
    );
  }

  // Whether the sub-nav has anything worth showing (tabs, filters, a warning,
  // a toggle, or a segmented control).
  shouldShowSubNav (config: any): boolean {
    const hasTabs = config.tabs?.length > 1;
    const hasFilters = config.filterConfigs?.some((fc: SubNavFilterConfig) => fc.showFilter && fc.filterOptions?.length);
    const hasSlideToggle = !!config.slideToggle?.show;
    const hasSegmentedControl = config.segmentedControl?.options?.length > 0;
    const hasWarning = !!config.showWarningMessage && !!config.warningMessage;
    return hasTabs || hasFilters || hasSlideToggle || hasSegmentedControl || hasWarning;
  }

  onSlideToggleChanged (checked: boolean): void {
    this.currentSlideToggleConfig?.onSlideToggleChange?.(checked);
  }

  onSegmentedControlChanged (index: number): void {
    this.currentSegmentedControlConfig?.onSegmentChange?.(index);
  }

  onActionButtonClicked (): void {
    // The action's behavior is owned by the page via the config's onClick callback.
    this.currentActionButtonConfig?.onClick?.();
  }

  ngOnDestroy (): void {
    this.documentPushObserver?.disconnect();
    this.documentPushUnlistenResize?.();
    this.documentPushResyncTimeouts.forEach(id => window.clearTimeout(id));
    // Nothing updates the offset once the watcher is gone, so a stale value would keep
    // pushing :root-level consumers (the environment indicator) after leaving this layout.
    this.renderer.removeStyle(document.documentElement, '--document-push-offset', RendererStyleFlags2.DashCase);
  }

  // Third-party top-of-page banners (Intercom's inline banners) push the document down by setting
  // a top margin on <body>. That moves the in-flow shell but not the viewport-fixed top nav, which
  // would stay hidden underneath the banner. Mirror the push distance into a CSS variable so the
  // nav's `top`, the shell's viewport-height math, and the over-mode side nav drawer follow it
  // (see the SCSS here and in global-side-nav).
  private watchDocumentPush (): void {
    this.ngZone.runOutsideAngular(() => {
      this.documentPushObserver = new MutationObserver(() => this.syncDocumentPushOffset());
      this.documentPushObserver.observe(document.body, { attributes: true, attributeFilter: ['style'], childList: true });

      // The banner's height is responsive (its text wraps on narrow viewports), and Intercom
      // re-applies the push after a resize through channels the <body> observer can't see, so
      // re-read the ground truth on resize — once right away, then again while Intercom's own
      // async recalculation settles.
      this.documentPushUnlistenResize = this.renderer.listen('window', 'resize', () => {
        this.documentPushResyncTimeouts.forEach(id => window.clearTimeout(id));
        this.documentPushResyncTimeouts = [0, 250, 1000].map(delay =>
          window.setTimeout(() => this.syncDocumentPushOffset(), delay));
      });
    });
    this.syncDocumentPushOffset();
  }

  private syncDocumentPushOffset (): void {
    // <body>'s border box starts below its own top margin, so its viewport top IS the push
    // distance, whatever mechanism applied it. Only consumed by CSS — no change detection needed.
    // Set on :root (not this element) because consumers outside the layout subtree — the
    // environment indicator in app.component — need it too.
    const pushOffset = Math.max(0, Math.round(document.body.getBoundingClientRect().top));
    // DashCase routes the write through style.setProperty, which a CSS custom property requires —
    // without the flag the renderer camel-cases the name and the write is silently dropped.
    this.renderer.setStyle(document.documentElement, '--document-push-offset', `${pushOffset}px`, RendererStyleFlags2.DashCase);
  }
}
